import type React from "react";
import { useState } from "react";

import { gatedModuleLabel } from "@dentacore/shared-types";
import type { GatedModule, ModuleName, PreviewModule } from "@dentacore/shared-types";

import { Modal } from "../ui/modal";
import styles from "./operator-page.module.css";
import { setClinicModule } from "./operator-api";
import type { ClinicDetail } from "./operator-api";

/** One sentence each, so the operator knows what they are switching (MOD-D2, MOD-D6). */
const WHAT: Record<ModuleName, string> = {
  // All stock is Supply since MS-D2: without it nothing is counted or deducted.
  SUPPLY:
    "Stock, procedure trays and the prep list, suppliers, supplier prices and orders. Without it, nothing is counted or taken off the shelf.",
  ACCOUNTING: "Accounting, and the bot sending a patient a bill.",
  CHATBOT:
    "The WhatsApp booking agent, the staff assistant, reminders and the booking-request queue.",
};

/** Previews are switched here too, but are not sold and carry no price (TK-D28). */
const PREVIEW_WHAT: Record<PreviewModule, string> = {
  TASKS:
    "The Tasks page: the team's jobs, typed by staff or raised by DentaCore. A preview — not priced, and off until switched on here.",
  WORKFLOWS:
    "The Workflows page: steps that run on their own over the clinic's records — from the quick ring, on a change, or on a schedule. A preview — not priced, and off until switched on here.",
};

/** What the switch dialog needs from an add-on or a preview. */
interface Switchable {
  module: GatedModule;
  enabled: boolean;
}

/**
 * Which add-on modules a clinic has, and the switch (STORY-188a, MOD-D1, MOD-D12).
 *
 * **Switching one off deletes nothing**, and the dialog says so before it does
 * anything — the clinic's records stay, hidden (MS-D10), and switching it back on
 * restores writing. **Chatbot's dialog adds the cost named in MOD-D16**:
 * reminders stop, which can raise no-shows.
 */
export function ModulesPanel({
  clinic,
  onChanged,
}: {
  clinic: ClinicDetail;
  onChanged: (next: ClinicDetail) => void;
}): React.JSX.Element {
  const [asking, setAsking] = useState<Switchable | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const apply = (target: Switchable): void => {
    setBusy(true);
    setError(null);
    void setClinicModule(clinic.id, target.module, !target.enabled)
      .then((next) => {
        setAsking(null);
        onChanged(next);
      })
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "Could not change the module");
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <section className={styles.panel} data-testid="modules-panel">
      <span className={styles.panelTitle}>Modules</span>
      <span className={styles.muted}>
        The base plan is always there. These are the add-ons this clinic has.
      </span>
      <ul className={styles.rows}>
        {clinic.modules.map((m) => (
          <li key={m.module} className={styles.row} data-testid={`module-${m.module}`}>
            <div className={styles.rowMain}>
              <span className={styles.rowName}>
                {gatedModuleLabel(m.module)} · {m.enabled ? "on" : "off"}
              </span>
              <span className={styles.rowSub}>{WHAT[m.module]}</span>
              {!m.enabled && m.requestedAt !== null && (
                <span className={styles.warn} data-testid={`module-asked-${m.module}`}>
                  Asked for on {new Date(m.requestedAt).toLocaleDateString()}
                </span>
              )}
              {m.changedAt !== null && (
                <span className={styles.rowSub}>
                  Switched {new Date(m.changedAt).toLocaleDateString()}
                  {m.changedByName === null ? "" : ` by ${m.changedByName}`}
                </span>
              )}
            </div>
            <button
              type="button"
              className={styles.ghost}
              data-testid={`module-switch-${m.module}`}
              onClick={() => {
                setError(null);
                setAsking(m);
              }}
            >
              {m.enabled ? "Switch off" : "Switch on"}
            </button>
          </li>
        ))}
      </ul>

      <span className={styles.panelTitle}>Previews</span>
      <span className={styles.muted}>
        Not sold and not priced. Off for every clinic until switched on here.
      </span>
      <ul className={styles.rows}>
        {clinic.previews.map((p) => (
          <li key={p.module} className={styles.row} data-testid={`module-${p.module}`}>
            <div className={styles.rowMain}>
              <span className={styles.rowName}>
                {gatedModuleLabel(p.module)} · {p.enabled ? "on" : "off"}
              </span>
              <span className={styles.rowSub}>{PREVIEW_WHAT[p.module]}</span>
              {p.changedAt !== null && (
                <span className={styles.rowSub}>
                  Switched {new Date(p.changedAt).toLocaleDateString()}
                  {p.changedByName === null ? "" : ` by ${p.changedByName}`}
                </span>
              )}
            </div>
            <button
              type="button"
              className={styles.ghost}
              data-testid={`module-switch-${p.module}`}
              onClick={() => {
                setError(null);
                setAsking(p);
              }}
            >
              {p.enabled ? "Switch off" : "Switch on"}
            </button>
          </li>
        ))}
      </ul>

      {asking !== null && (
        <Modal
          label={`${asking.enabled ? "Switch off" : "Switch on"} ${gatedModuleLabel(asking.module)}`}
          onClose={() => {
            setAsking(null);
          }}
        >
          <div className={styles.dialogBody}>
            <h2 className={styles.h2}>
              {asking.enabled ? "Switch off" : "Switch on"} {gatedModuleLabel(asking.module)} for{" "}
              {clinic.name}?
            </h2>
            {asking.enabled ? (
              <>
                <p className={styles.muted}>
                  <strong>Nothing is deleted.</strong>{" "}
                  {asking.module === "CHATBOT"
                    ? "Their messages and booking requests stay, and the bots stop."
                    : "Their records stay, hidden, and come back when it is switched on again. Until then the clinic sees it exactly as if it had never had it (MS-D10)."}
                </p>
                {asking.module === "CHATBOT" && (
                  <p className={styles.warn} data-testid="chatbot-off-warning">
                    <strong>Appointment reminders stop</strong>, and the bots stop answering
                    patients. That can raise no-shows. Patients&rsquo; messages are kept for staff
                    to read.
                  </p>
                )}
              </>
            ) : (
              <p className={styles.muted}>
                It takes effect on their next click. Nobody needs to sign out.
              </p>
            )}
            {error !== null && (
              <p className={styles.error} role="alert">
                {error}
              </p>
            )}
            <div className={styles.dialogActions}>
              <button
                type="button"
                className={styles.ghost}
                onClick={() => {
                  setAsking(null);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className={asking.enabled ? styles.danger : styles.primary}
                disabled={busy}
                data-testid="module-confirm"
                onClick={() => {
                  apply(asking);
                }}
              >
                {busy ? "Saving…" : asking.enabled ? "Switch off" : "Switch on"}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </section>
  );
}
